import React, { useState } from "react";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc, collection, query, where, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import starrySky from "../assets/starry-sky.jpg";

const Signup = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const getAgeGroup = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
    if (age >= 1 && age <= 12) return "Kids";
    if (age >= 13 && age <= 17) return "Teens";
    return "Adults";
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!dateOfBirth) {
      setError("Please select a date of birth.");
      return;
    }

    // Check if username is already taken
    try {
      const q = query(collection(db, "users"), where("username", "==", username));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        setError("Username is already taken. Please choose a different one.");
        return;
      }
    } catch (err) {
      setError("Error checking username availability. Please try again.");
      console.error("Username check error:", err);
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const ageGroup = getAgeGroup(dateOfBirth);

      // Debug log to confirm write attempt
      console.log("Attempting to create document for user:", user.uid, {
        email,
        username,
        date_of_birth: dateOfBirth,
        age_group: ageGroup,
        timestamp: new Date(),
      });

      // Use lowercase "users" to match firestore.rules
      await setDoc(doc(db, "users", user.uid), {
        email: user.email,
        username,
        date_of_birth: dateOfBirth,
        age_group: ageGroup,
        timestamp: new Date(),
      });

      console.log("Document created successfully for user:", user.uid);
      navigate("/");
    } catch (err) {
      setError(`Failed to create account: ${err.message}`);
      console.error("Signup error details:", err);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{
        backgroundImage: `url(${starrySky})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="bg-white bg-opacity-80 p-8 rounded-lg shadow-lg w-full max-w-md">
        <img src="/logo512.png" alt="Rocket Trail Logo" className="mx-auto mb-4 w-16 h-16" />
        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Sign Up for Rocket Trail</h2>
        <form onSubmit={handleSignup}>
          <div className="mb-4">
            <label className="block text-gray-800 mb-2" htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-800 mb-2" htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-800 mb-2" htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800"
              required
            />
          </div>
          <div className="mb-6">
            <label className="block text-gray-800 mb-2" htmlFor="date_of_birth">Date of Birth</label>
            <input
              type="date"
              id="date_of_birth"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800"
              required
              max={new Date().toISOString().split("T")[0]} // Restrict to today or earlier
            />
          </div>
          {error && <p className="text-red-500 mb-4">{error}</p>}
          <button
            type="submit"
            className="w-full bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
          >
            Sign Up
          </button>
        </form>
        <p className="text-gray-800 mt-4 text-center">
          Already have an account? <a href="/login" className="text-blue-500 hover:underline">Login</a>
        </p>
      </div>
    </div>
  );
};

export default Signup;
